import { useMemo } from 'react';
import { type InsightsTracksResponse } from '@internal/backstage-plugin-pulse-common';
import { useInsightsTracks, useMyTeams } from '../../hooks';
import { expandOwnerScopeRefs } from '../insights/insights-group-scope';

export type PulseTeamInsights = {
  /** The user's own groups, i.e. what the insights page should preselect. */
  selectedOwnerRefs: string[];
  /** `selectedOwnerRefs` plus every descendant group, matching the insights scope. */
  ownerScopeRefs: string[];
  teamLabels: string[];
  insights?: InsightsTracksResponse;
};

/**
 * Loads the aggregated Pulse track compliance for the signed-in user's teams.
 *
 * Resolves the groups through `useMyTeams` and expands them to descendants the
 * same way the insights page does, so both views report the same numbers.
 */
export const usePulseTeamInsights = () => {
  const {
    ownerByRef,
    labelByRef,
    teamRefs,
    ownedGroupRefs,
    loading: teamsLoading,
  } = useMyTeams();

  // Prefer actual teams over the areas/units the user also owns.
  const selectedOwnerRefs = useMemo(
    () => (teamRefs.length > 0 ? teamRefs : ownedGroupRefs),
    [teamRefs, ownedGroupRefs],
  );

  const ownerScopeRefs = useMemo(
    () => expandOwnerScopeRefs(selectedOwnerRefs, ownerByRef),
    [selectedOwnerRefs, ownerByRef],
  );

  const {
    value: insights,
    loading: insightsLoading,
    error,
  } = useInsightsTracks(ownerScopeRefs);

  const teamLabels = useMemo(
    () =>
      selectedOwnerRefs.map(ownerRef => labelByRef.get(ownerRef) ?? ownerRef),
    [selectedOwnerRefs, labelByRef],
  );

  const value = useMemo<PulseTeamInsights>(
    () => ({ selectedOwnerRefs, ownerScopeRefs, teamLabels, insights }),
    [selectedOwnerRefs, ownerScopeRefs, teamLabels, insights],
  );

  return {
    value,
    loading: teamsLoading || insightsLoading,
    error,
  };
};
